import {
  closestCenter,
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { createContext, useCallback, useContext, useState } from "react";
import { useThreadMoveContext } from "./ThreadMoveContext";
import { useSidebarDndHandlers } from "@/features/sidebar/hooks/useSidebarDndHandlers";

const SidebarDndContext = createContext();

export const useSidebarDndContext = () => {
  const context = useContext(SidebarDndContext);
  if (!context) {
    throw new Error(
      "useSidebarDndContext must be used within a SidebarDndProvider"
    );
  }
  return context;
};

export function SidebarDndProvider({ children }) {
  const [activeId, setActiveId] = useState(null);
  const [draggedItem, setDraggedItem] = useState(null);
  const [draggedItemType, setDraggedItemType] = useState(null); // 'project' | 'chat' | 'file'
  const [dragSource, setDragSource] = useState(null); // 'sidebar' | 'project' - where the drag originated
  const [isValidDrop, setIsValidDrop] = useState(false); // Track if last drop was valid

  // Get isThreadMoving from context
  const { isThreadMoving } = useThreadMoveContext() || {};
  const checkIsThreadMoving = isThreadMoving || (() => false);

  // Get centralized drag-end handler
  const { handleDragEnd: handleDropAction } = useSidebarDndHandlers();

  // Configure sensors with activation constraints
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 5, // 5px movement required to start drag
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 150, // 150ms delay for touch
        tolerance: 5, // 5px tolerance to prevent scroll conflicts
      },
    })
  );

  const handleDragStart = useCallback(
    (event) => {
      const { active } = event;

      // Extract item data from the active item
      const itemData = active.data.current;

      // Prevent drag start if this thread is currently being moved
      if (itemData?.type === "chat" && itemData?.item?.id) {
        const threadId = itemData.item.id;
        if (checkIsThreadMoving(threadId)) {
          console.warn(
            `Cannot start drag: thread ${threadId} is currently being moved`
          );
          return; // Cancel the drag
        }
      }

      setActiveId(active.id);

      if (itemData) {
        setDraggedItem(itemData.item);
        setDraggedItemType(itemData.type);
        setDragSource(itemData.source || null); // 'sidebar' or 'project'
      }

      // Prevent scrolling during drag
      document.body.classList.add("drag-in-progress");
      document.body.style.overflow = "hidden";
      document.body.style.userSelect = "none";
    },
    [checkIsThreadMoving]
  );

  const handleDragEnd = useCallback(
    (event) => {
      const { active, over } = event;

      // Restore scrolling
      document.body.classList.remove("drag-in-progress");
      document.body.style.overflow = "";
      document.body.style.userSelect = "";

      if (!over) {
        // Reset drag state immediately if dropped outside (invalid target)
        setActiveId(null);
        setDraggedItem(null);
        setDraggedItemType(null);
        setDragSource(null);
        return;
      }

      const activeData = active.data.current;
      const overData = over.data.current;

      // Determine if this is a valid drop target based on more comprehensive checks
      let dropIsValid = false;

      if (activeData && overData) {
        // Project reordering: valid if both are projects and different
        if (
          activeData.type === "project" &&
          overData.type === "project" &&
          active.id !== over.id
        ) {
          dropIsValid = true;
        }
        // Chat reordering: valid if same workspace and different positions
        else if (
          activeData.type === "chat" &&
          overData.type === "chat" &&
          activeData.workspaceId === overData.workspaceId &&
          active.id !== over.id
        ) {
          dropIsValid = true;
        }
        // Chat moving to workspace or Chats: valid if dropping on workspace/project/chat in different workspace, or on Chats section
        else if (activeData.type === "chat") {
          const sourceWorkspaceId = activeData.workspaceId || null;
          let targetWorkspaceId = null;

          // Handle drop on Chats section (move from project to Chats)
          if (overData.type === "chats") {
            // Valid if chat is from a project (has a workspace)
            if (sourceWorkspaceId !== null) {
              dropIsValid = true;
            }
          } else if (overData.type === "workspace" && overData.workspaceId) {
            targetWorkspaceId = overData.workspaceId;
          } else if (overData.type === "project" && overData.item?.id) {
            targetWorkspaceId = overData.item.id;
          } else if (overData.type === "chat" && overData.workspaceId) {
            targetWorkspaceId = overData.workspaceId;
          }

          // Valid if moving to a different workspace
          if (targetWorkspaceId && targetWorkspaceId !== sourceWorkspaceId) {
            dropIsValid = true;
          }
        }
      }

      // Store validity for use in DragOverlay
      setIsValidDrop(dropIsValid);

      // Call centralized drag-end handler
      handleDropAction(event);

      // If valid drop, delay reset to allow animation to target
      // If invalid drop, reset immediately to show return animation
      if (dropIsValid) {
        // For valid drops, we want the overlay to fade out smoothly
        // rather than animating back to source. We'll keep it visible
        // until the sortable items have finished their transition.
        // @dnd-kit sortable transitions are typically 200-300ms

        // Start fade-out after a short delay (allows sortable animation to begin)
        setTimeout(() => {
          setIsValidDrop(true); // Trigger fade-out CSS transition
        }, 100);

        // Clear state after fade-out completes
        setTimeout(() => {
          setActiveId(null);
          setDraggedItem(null);
          setDraggedItemType(null);
          setDragSource(null);
          setIsValidDrop(false);
        }, 400); // Total delay: 100ms to start fade + 300ms fade duration
      } else {
        // Reset immediately for invalid drops (shows return animation)
        setActiveId(null);
        setDraggedItem(null);
        setDraggedItemType(null);
        setDragSource(null);
        setIsValidDrop(false);
      }
    },
    [handleDropAction]
  );

  const handleDragCancel = useCallback(() => {
    // Restore scrolling on cancel
    document.body.classList.remove("drag-in-progress");
    document.body.style.overflow = "";
    document.body.style.userSelect = "";

    setActiveId(null);
    setDraggedItem(null);
    setDraggedItemType(null);
    setDragSource(null);
  }, []);

  const contextValue = {
    activeId,
    draggedItem,
    draggedItemType,
    dragSource, // 'sidebar' | 'project' | null
    isDragging: activeId !== null,
    isThreadMoving: checkIsThreadMoving,
  };

  return (
    <SidebarDndContext.Provider value={contextValue}>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        {children}
        <DragOverlay
          dropAnimation={
            isValidDrop
              ? null // Disable drop animation for valid drops (we'll fade out manually)
              : {
                  duration: 200,
                  easing: "cubic-bezier(0.18, 0.67, 0.6, 1.22)",
                }
          }
        >
          {draggedItem && draggedItemType ? (
            <div
              className={`bg-sidebar-accent border border-border rounded-md p-2 shadow-lg max-w-[240px] transition-opacity duration-300 ${
                isValidDrop ? "opacity-0" : "opacity-90"
              }`}
            >
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-purple-500 rounded-full flex-shrink-0"></div>
                <span className="text-sm font-medium text-sidebar-accent-foreground truncate">
                  {draggedItem.name ||
                    draggedItem.title ||
                    draggedItem.file_name ||
                    "Item"}
                </span>
              </div>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
    </SidebarDndContext.Provider>
  );
}
